
// Function for querying the plant asset table
export const queryTableAssetData = (tableAssetData, clickedId, clickedDepth) => {
    if (clickedDepth === 0) {
        // Top level (Vineyard)
        return tableAssetData.find(vineyard => vineyard.id === clickedId);
    } else if (clickedDepth === 1) {
        // Sub-Vineyard
        for (const vineyard of tableAssetData) {
            const subVineyard = vineyard.subRows.find(sub => sub.id === clickedId);
            if (subVineyard) return subVineyard;
        }
    } else if (clickedDepth === 2) {
        // Block
        for (const vineyard of tableAssetData) {
            for (const subVineyard of vineyard.subRows) {
                const block = subVineyard.subRows.find(b => b.id === clickedId);
                if (block) return block;
            }
        }
    }

    return null; // Not found
};

// Function for calculating blocks with minimum and maximum KPI (e.g. productivity)
export function findMinMaxBlocks(blocks, numeratorKey, denominatorKeys, rowDepth) {
    let workingBlocks = blocks;
    if (rowDepth === 0 && blocks.subRows) {
      workingBlocks = blocks.subRows;
    }
    // Keep the existing logic unchanged
    if (Array.isArray(workingBlocks)) {
      console.log("Processing as an array"); // Debug log
      let maxRatio = -Infinity;
      let minRatio = Infinity;
      let maxBlock = null;
      let minBlock = null;
  
      workingBlocks.forEach(block => {
        const numerator = block[numeratorKey];
        const denominator = denominatorKeys.reduce((acc, key) => acc + (block[key] || 0), 0);
        if (denominator !== 0) {
          const ratio = numerator / denominator;
          if (ratio > maxRatio) {
            maxRatio = ratio;
            maxBlock = block;
          }
          if (ratio < minRatio) {
            minRatio = ratio;
            minBlock = block;
          }
        }
      });
  
      if (maxBlock && minBlock) {
        return { maxBlock: maxBlock.id || maxBlock.block_id, minBlock: minBlock.id || minBlock.block_id };
      }
    }
    return { maxBlock: undefined, minBlock: undefined };
  }
  
// KPI text map for rendering human readable dashboard
export const productivePlantKPITextMap = {
    'vineyards': 'across',
    'blocks': 'across',
    'block': 'within'
};

export const transformToHierarchical = (data) => {
    // Check if data is null or undefined and return an empty array if it is
    if (!data) return [{
        'yield': 'no_data',
        'revenue': 'no_data',
        'cost': 'no_data',
      }];

    return data.map(vineyard => {
        // Check if vineyard.children is null or undefined before mapping
        const children = vineyard.children ? vineyard.children.map(child => ({
            block_id: child.block_id,
            cost: child.cost === "no_data" ? 0 : parseFloat(child.cost),
            revenue: child.revenue === "no_data" ? 0 : parseFloat(child.revenue),
            yield: child.yield === "no_data" ? 0 : parseFloat(child.yield),
            canopy_threshold: child.canopy_threshold === "no_data" ? 0 : parseFloat(child.canopy_threshold),
            yield_threshold: child.yield_threshold === "no_data" ? 0 : parseFloat(child.yield_threshold),
        })) : [];
        
        // Calculate totals, safely assuming children is an array (even if empty)
        const cost = children.reduce((acc, child) => acc + (isNaN(parseFloat(child.cost)) ? 0 : parseFloat(child.cost)), 0);
        const revenue = children.reduce((acc, child) => acc + (isNaN(parseFloat(child.revenue)) ? 0 : parseFloat(child.revenue)), 0);
        const yieldVal = children.reduce((acc, child) => acc + (isNaN(parseFloat(child.yield)) ? 0 : parseFloat(child.yield)), 0);

        return {
            vineyard_id: vineyard.vineyard_id,
            cost: cost,
            revenue: revenue,
            yield: yieldVal,
            children: children,
        };
    });
};

export const mergeCanopyvolumeYieldFields = (dataFull, maturityDataFull) => {
    // Step 1: Create a map from geoJsonVineDataFull for quick lookup
    const dataFullMap = new Map();
    dataFull.features.forEach(feature => {
      dataFullMap.set(feature.id, feature);
    });
  
    // Step 2: Merge data into geoJsonVineMaturityDataFull
    maturityDataFull.features = maturityDataFull.features.map(feature => {
      const correspondingFeature = dataFullMap.get(feature.id);
      if (correspondingFeature) {
        // Check and merge 'canopy_volume_index_0d' if it exists
        if ('canopy_volume_index_0d' in correspondingFeature.properties) {
          feature.properties.canopy_volume_index_0d = correspondingFeature.properties.canopy_volume_index_0d;
        }
  
        // Check and merge 'yield_index' if it exists
        if ('yield_index' in correspondingFeature.properties) {
          feature.properties.yield_index = correspondingFeature.properties.yield_index;
        }
      }
      return feature;
    });
  
    return maturityDataFull;
  };

  export const filterAndUpdateFeatures = (pointFeatures, thresholdValues, targetMaturityClass, newMaturityClass) => {
    // Convert the threshold array into a more accessible format
    const blockThresholds = {};
    thresholdValues.children.forEach(block => {
        blockThresholds[block.block_id] = {
            yield_index: block.yield_threshold,
            canopy_volume_index: block.canopy_threshold
        };
    });
  
    console.log('blockThresholds:', blockThresholds);
  
    pointFeatures.features.forEach(feature => {
      const yieldIndex = feature.properties.yield_index;
      const canopyVolumeIndex = feature.properties.canopy_volume_index_0d;
      const blockName = feature.properties.block_name;
  
      if (yieldIndex !== undefined && blockThresholds[blockName] && blockThresholds[blockName].yield_index !== undefined) {
          if (yieldIndex >= blockThresholds[blockName].yield_index) {
              feature.properties.class_fruit_rating = 'StrongFruit';
          } else {
              feature.properties.class_fruit_rating = 'WeakFruit';
          }
      }
  
      if (canopyVolumeIndex !== undefined && blockThresholds[blockName] && blockThresholds[blockName].canopy_volume_index !== undefined) {
          if (canopyVolumeIndex >= blockThresholds[blockName].canopy_volume_index) {
              feature.properties.class_canopy_rating = 'StrongCanopy';
          } else {
              feature.properties.class_canopy_rating = 'WeakCanopy';
          }
      }
    });
  
    // Reset features from newMaturityClass back to targetMaturityClass
    pointFeatures.features.forEach(feature => {
        if (feature.properties.class_maturity === newMaturityClass) {
            feature.properties.class_maturity = targetMaturityClass;
        }
    });
  
    pointFeatures.features.forEach(feature => {
        if (feature.properties.class_maturity === targetMaturityClass) {
            const blockName = feature.properties.block_name;
            const yieldIndex = feature.properties.yield_index;
            const canopyVolumeIndex = feature.properties.canopy_volume_index_0d;
  
            // Get the threshold values for the block
            const blockThreshold = blockThresholds[blockName];
            if (blockThreshold) {
                const isYieldBelowThreshold = yieldIndex !== undefined && yieldIndex < blockThreshold.yield_index;
                const isCanopyVolumeBelowThreshold = canopyVolumeIndex !== undefined && canopyVolumeIndex < blockThreshold.canopy_volume_index;
  
                // Check if either of the properties is below the threshold
                if (isYieldBelowThreshold || isCanopyVolumeBelowThreshold) {
                    // Update class_maturity to the new class
                    feature.properties.class_maturity = newMaturityClass;
                }
            }
        }
    });
  };
  